import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Upload, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang } from "@/i18n/LanguageContext";
import { getDonorWall, uploadDonorPhoto, fullUrl } from "@/lib/api";

export const DonorWall = () => {
  const { t } = useLang();
  const [wall, setWall] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", amount: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => getDonorWall().then(setWall).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.amount) return toast.error(t("donate.fillDetails"));
    if (Number(form.amount) < 10000) return toast.error(t("wall.minError"));
    if (!file) return toast.error(t("wall.needPhoto"));
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("amount", form.amount);
      fd.append("file", file);
      await uploadDonorPhoto(fd);
      toast.success(t("wall.success"));
      setOpen(false); setForm({ name: "", amount: "" }); setFile(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="donor-wall" data-testid="donor-wall-section" className="py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-primary mb-5">{t("wall.overline")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-foreground leading-tight mb-5">
              {t("wall.title")}
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">{t("wall.desc")}</p>
          </div>
          <button
            data-testid="wall-add-btn"
            onClick={() => setOpen(true)}
            className="shrink-0 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-semibold inline-flex items-center gap-2 hover:scale-105 transition-transform w-fit"
          >
            <Sparkles size={16} /> {t("wall.addPhoto")}
          </button>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3 sm:gap-4">
          {wall.map((d, i) => (
            <motion.div
              key={d.id}
              initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 8) * 0.05 }}
              data-testid={`wall-tile-${i}`}
              className="group relative aspect-square rounded-xl overflow-hidden border border-border"
            >
              <img src={fullUrl(d.url)} alt={d.name} className="w-full h-full object-cover grayscale-[15%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(160_38%_16%)]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 inset-x-0 p-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-white text-xs font-semibold truncate">{d.name}</p>
                <p className="text-secondary text-[9px] uppercase tracking-wider">{t("wall.featured")}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {wall.length === 0 && (
          <div data-testid="wall-empty" className="border border-dashed border-border rounded-2xl py-16 px-6 text-center">
            <Sparkles size={28} className="text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-serif text-3xl text-foreground mb-2">{t("wall.empty")}</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">{t("wall.emptyDesc")}</p>
            <button
              data-testid="wall-empty-add-btn"
              onClick={() => setOpen(true)}
              className="rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 hover:scale-105 transition-transform"
            >
              <Sparkles size={16} /> {t("wall.addPhoto")}
            </button>
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <motion.form
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="wall-dialog"
            className="w-full max-w-md bg-card rounded-2xl p-7 sm:p-8 relative"
          >
            <button type="button" onClick={() => setOpen(false)} className="absolute top-5 right-5 text-muted-foreground hover:text-foreground">
              <X size={20} />
            </button>
            <h3 className="font-serif text-3xl text-foreground mb-2">{t("wall.dialogTitle")}</h3>
            <p className="text-sm text-muted-foreground mb-6">{t("wall.dialogDesc")}</p>

            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("wall.name")}</Label>
                <Input data-testid="wall-name" className="mt-2 bg-white/60" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("wall.donationAmount")}</Label>
                <Input data-testid="wall-amount" type="number" min="10000" className="mt-2 bg-white/60" value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="10000" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("wall.choosePhoto")}</Label>
                <label className="mt-2 flex items-center gap-3 rounded-xl border border-dashed border-input bg-white/60 px-4 py-3 text-sm cursor-pointer hover:border-primary transition-colors">
                  <Upload size={16} className="text-primary" />
                  <span className="truncate text-muted-foreground">{file ? file.name : t("wall.choosePhoto")}</span>
                  <input data-testid="wall-file" type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                         onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
              </div>
              <button data-testid="wall-submit" disabled={loading}
                      className="w-full rounded-full bg-primary text-primary-foreground py-3.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:scale-[1.01] transition-transform">
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? t("wall.uploading") : t("wall.submit")}
              </button>
            </div>
          </motion.form>
        </div>
      )}
    </section>
  );
};
